import type { FC, ReactNode } from 'react'
import type { GetServerSideProps } from 'next'
import type { LayoutData, MoodFeed, PageContent } from '@/types/content'

import Link from 'next/link'
import { ArrowUpRight } from 'lucide-react'
import { cms } from '@/lib/cms'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { PageHero } from '@/components/site/page-hero'
import { MoonPhase, phaseForIndex } from '@/components/media/moon-phase'
import { EmotionFinder } from '@/components/emotions/emotion-finder'
import { getLayout, getPage } from '@/lib/server/content/site'
import { getMoodFeeds } from '@/lib/server/content/moods'
import { queryParam, setPageCache } from '@/lib/server/page'

interface EmotionsPageProps {
    layout: LayoutData
    page: PageContent | null
    feeds: Array<MoodFeed>
    initial: string
}

const EmotionsPage: FC<EmotionsPageProps> = ({ layout, page, feeds, initial }): ReactNode => {
    return (
        <Layout layout={layout}>
            <Seo
                title={page?.seo_title ?? 'Emotion Finder'}
                description={page?.seo_description}
                ogImage={page?.og_image ?? layout.settings.og_image}
                ogImageAlt={page?.og_image_alt}
                origin={layout.origin}
                path='/emotions'
            />

            <PageHero page={page} fallback={{ eyebrow: 'Emotion finder', heading: 'Find music by feeling' }} phase={0.5} image='/images/misty-forest.jpg' imageAlt='Pine trees fading into morning mist' />

            <section id='emotion-finder' aria-label='Emotion finder' className='shell pb-20'>
                <EmotionFinder feeds={feeds} initialSlug={initial} />
            </section>

            <section id='all-emotions' aria-labelledby='all-emotions-title' className='border-t border-white/[0.06] py-20'>
                <div className='shell'>
                    <h2 id='all-emotions-title' className='font-heading text-4xl font-light'>
                        Every feeling
                    </h2>

                    <ul className='mt-10 grid gap-px overflow-hidden rounded-2xl border border-white/[0.07] bg-white/[0.07] sm:grid-cols-2 lg:grid-cols-4'>
                        {feeds.map((feed, index) => (
                            <li key={feed.mood.id} className='bg-obsidian'>
                                <Link href={`/emotions/${feed.mood.slug}`} className='group flex h-full flex-col gap-6 p-7 transition hover:bg-charcoal'>
                                    <div className='flex items-center justify-between'>
                                        <MoonPhase phase={phaseForIndex(index, feeds.length)} size={32} color={feed.mood.color ?? '#E9E4DA'} />
                                        <ArrowUpRight className='size-5 text-ivory/30 transition group-hover:text-gold' aria-hidden='true' />
                                    </div>

                                    <div>
                                        <p className='font-heading text-2xl leading-tight'>
                                            <span {...cms('moods', feed.mood.id, 'name')}>{feed.mood.name}</span>
                                        </p>

                                        {feed.mood.prompt && (
                                            <p className='mt-2 font-heading text-lg italic text-ivory/60'>
                                                <span {...cms('moods', feed.mood.id, 'prompt')}>{feed.mood.prompt}</span>
                                            </p>
                                        )}

                                        <p className='mt-4 text-xs uppercase tracking-[0.16em] text-ivory/35'>
                                            {feed.songs.length} {feed.songs.length === 1 ? 'song' : 'songs'} &middot; {feed.writings.length} {feed.writings.length === 1 ? 'writing' : 'writings'} &middot; {feed.videos.length} {feed.videos.length === 1 ? 'film' : 'films'}
                                        </p>
                                    </div>
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </section>
        </Layout>
    )
}

export const getServerSideProps: GetServerSideProps<EmotionsPageProps> = async ctx => {
    const [layout, page, feeds] = await Promise.all([getLayout(ctx.req), getPage('emotions'), getMoodFeeds({ songs: 6, writings: 4, videos: 4 })])

    setPageCache(ctx)

    return { props: { layout, page, feeds, initial: queryParam(ctx.query.feeling) } }
}

export default EmotionsPage
